import React, { useState } from 'react';
import { Button } from '../ui/button';
import { 
  Share2, 
  Facebook, 
  Twitter, 
  MessageCircle, 
  Link2, 
  Mail,
  Check,
  X
} from 'lucide-react';
import { toast } from 'sonner';

export function ShareButtons({ tour, className = '' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = `Check out this amazing tour: ${tour.title}`;
  const shareTextEncoded = encodeURIComponent(shareText);
  const shareUrlEncoded = encodeURIComponent(shareUrl);

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: tour.title,
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          setIsOpen(true);
        }
      }
    } else {
      setIsOpen(true);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const shareLinks = [
    {
      name: 'WhatsApp',
      icon: MessageCircle,
      color: 'bg-green-500 hover:bg-green-600',
      url: `https://wa.me/?text=${shareTextEncoded}%20${shareUrlEncoded}`,
    },
    {
      name: 'Facebook',
      icon: Facebook,
      color: 'bg-blue-600 hover:bg-blue-700',
      url: `https://www.facebook.com/sharer/sharer.php?u=${shareUrlEncoded}`,
    },
    {
      name: 'Twitter',
      icon: Twitter,
      color: 'bg-sky-500 hover:bg-sky-600',
      url: `https://twitter.com/intent/tweet?text=${shareTextEncoded}&url=${shareUrlEncoded}`,
    },
    {
      name: 'Email',
      icon: Mail,
      color: 'bg-gray-600 hover:bg-gray-700',
      url: `mailto:?subject=${encodeURIComponent(tour.title)}&body=${shareTextEncoded}%20${shareUrlEncoded}`,
    },
  ];

  return (
    <div className={`relative ${className}`}>
      {/* Share Button */}
      <Button
        variant="outline"
        size="sm"
        onClick={handleNativeShare}
        className="gap-2 rounded-full"
        data-testid="share-button"
      >
        <Share2 className="h-4 w-4" />
        Share
      </Button>

      {/* Share Modal/Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40 bg-black/20" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Share Panel */}
          <div className="absolute right-0 top-12 z-50 w-72 bg-white rounded-xl shadow-xl border border-gray-200 p-4 animate-in slide-in-from-top-2 duration-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Share this tour</h3>
              <button 
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Social Share Buttons */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              {shareLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-white text-sm font-medium transition-colors ${link.color}`}
                  onClick={() => setIsOpen(false)}
                  data-testid={`share-${link.name.toLowerCase()}`}
                >
                  <link.icon className="h-4 w-4" />
                  {link.name}
                </a>
              ))}
            </div>

            {/* Copy Link */}
            <div className="border-t pt-4">
              <p className="text-xs text-gray-500 mb-2">Or copy link</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  className="flex-1 px-3 py-2 text-xs bg-gray-100 rounded-lg border border-gray-200 truncate"
                />
                <Button
                  size="sm"
                  variant={copied ? "default" : "outline"}
                  onClick={handleCopyLink}
                  className={`shrink-0 ${copied ? 'bg-green-500 hover:bg-green-500' : ''}`}
                  data-testid="copy-link-btn"
                >
                  {copied ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Link2 className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Compact inline share buttons for mobile/footer
export function InlineShareButtons({ tour, className = '' }) {
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = encodeURIComponent(`Check out this amazing tour: ${tour.title}`);
  const shareUrlEncoded = encodeURIComponent(shareUrl);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied!');
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <span className="text-sm text-muted-foreground mr-1">Share:</span>
      <a
        href={`https://wa.me/?text=${shareText}%20${shareUrlEncoded}`}
        target="_blank"
        rel="noopener noreferrer"
        className="p-2 rounded-full bg-green-100 text-green-600 hover:bg-green-200 transition-colors"
        title="Share on WhatsApp"
      >
        <MessageCircle className="h-4 w-4" />
      </a>
      <a
        href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrlEncoded}`}
        target="_blank"
        rel="noopener noreferrer"
        className="p-2 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
        title="Share on Facebook"
      >
        <Facebook className="h-4 w-4" />
      </a>
      <a
        href={`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrlEncoded}`}
        target="_blank"
        rel="noopener noreferrer"
        className="p-2 rounded-full bg-sky-100 text-sky-600 hover:bg-sky-200 transition-colors"
        title="Share on Twitter"
      >
        <Twitter className="h-4 w-4" />
      </a>
      <button
        onClick={handleCopyLink}
        className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
        title="Copy link"
      >
        <Link2 className="h-4 w-4" />
      </button>
    </div>
  );
}
